<script setup lang="ts">
import { watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { PhList, PhMoon, PhSun, PhX } from '@phosphor-icons/vue'
import { useDarkMode } from '@/composables/useDarkMode'
import { brandLogoUrl, siteNav } from '@/data/ecosystem'

const route = useRoute()
const { isDark, toggle: toggleDarkMode } = useDarkMode()

watch(
  () => route.fullPath,
  () => {
    const panel = document.querySelector<HTMLElement>('#hs-navbar-collapse')
    const toggle = document.querySelector<HTMLElement>('#hs-navbar-collapse-toggle')
    if (panel && !panel.classList.contains('hidden') && window.innerWidth < 1024) {
      panel.classList.add('hidden')
      panel.classList.remove('open')
      toggle?.classList.remove('open')
      toggle?.setAttribute('aria-expanded', 'false')
    }
  },
)
</script>

<template>
  <header
    class="sticky top-0 z-50 flex w-full border-b border-border/50 bg-background/85 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70"
  >
    <nav
      class="relative mx-auto flex w-full max-w-6xl flex-wrap items-center basis-full gap-x-4 gap-y-3 px-4 py-3 sm:px-6 sm:py-4 lg:grid lg:grid-cols-12 lg:gap-x-0 lg:px-8"
    >
      <!-- Brand + mobile toggle -->
      <div class="flex w-full items-center justify-between lg:col-span-3 lg:w-auto">
        <RouterLink
          to="/"
          class="group inline-flex shrink-0 items-center transition"
          aria-label="As-Safwah home"
        >
          <img
            :src="brandLogoUrl"
            alt="As-Safwah Inc."
            class="h-11 w-11 rounded-full bg-background object-contain shadow-sm ring-1 ring-border/60 transition-all duration-500 ease-luxury group-hover:scale-110 group-hover:ring-primary/50 group-hover:shadow-[0_0_0_6px_hsl(var(--primary)/0.12)] sm:h-12 sm:w-12"
            width="48"
            height="48"
          />
        </RouterLink>

        <div class="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            class="inline-flex size-9 items-center justify-center rounded-full transition-all duration-300 hover:rotate-12 hover:bg-muted hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-label="Toggle dark mode"
            @click="toggleDarkMode()"
          >
            <PhSun v-if="isDark" :size="18" />
            <PhMoon v-else :size="18" />
          </button>
          <button
            type="button"
            id="hs-navbar-collapse-toggle"
            class="hs-collapse-toggle inline-flex size-9 items-center justify-center rounded-full border border-border/70 transition hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-expanded="false"
            aria-controls="hs-navbar-collapse"
            aria-label="Toggle navigation"
            data-hs-collapse="#hs-navbar-collapse"
          >
            <PhList class="hs-collapse-open:hidden size-[22px] shrink-0" />
            <PhX class="hs-collapse-open:block hidden size-[22px] shrink-0" />
          </button>
        </div>
      </div>

      <!-- Collapse nav -->
      <div
        id="hs-navbar-collapse"
        class="hs-collapse hidden w-full basis-full overflow-hidden transition-[height] duration-300 lg:order-2 lg:col-span-6 lg:block lg:w-auto lg:basis-auto lg:overflow-visible"
        aria-labelledby="hs-navbar-collapse-toggle"
      >
        <div
          class="mt-3 flex flex-col gap-1 border-t border-border/50 pt-3 lg:mt-0 lg:flex-row lg:items-center lg:justify-center lg:gap-x-7 lg:gap-y-0 lg:border-0 lg:pt-0"
        >
          <RouterLink
            v-for="item in siteNav"
            :key="item.to"
            :to="item.to"
            class="nav-link-lux rounded-xl px-3 py-2.5 text-sm font-medium lg:rounded-none lg:px-0 lg:py-0"
            :active-class="item.to === '/' ? undefined : 'router-link-active'"
            :exact-active-class="item.to === '/' ? 'router-link-exact-active' : undefined"
          >
            {{ item.label }}
          </RouterLink>
          <RouterLink
            to="/terms"
            class="rounded-xl px-3 py-2.5 text-sm font-medium text-foreground transition hover:bg-muted hover:text-secondary lg:hidden"
          >
            Terms &amp; Privacy
          </RouterLink>
          <RouterLink
            to="/contact"
            class="btn-glow mt-1 block rounded-xl bg-secondary px-3 py-3 text-center text-sm font-semibold text-secondary-foreground lg:hidden"
          >
            Contact
          </RouterLink>
        </div>
      </div>

      <!-- Desktop actions -->
      <div class="ms-auto hidden items-center gap-2 lg:order-3 lg:col-span-3 lg:flex lg:justify-end">
        <button
          type="button"
          class="inline-flex size-9 items-center justify-center rounded-full transition-all duration-300 hover:rotate-12 hover:bg-muted hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          aria-label="Toggle dark mode"
          @click="toggleDarkMode()"
        >
          <PhSun v-if="isDark" :size="18" />
          <PhMoon v-else :size="18" />
        </button>
        <RouterLink
          to="/contact"
          class="btn-glow inline-flex rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground"
        >
          Contact
        </RouterLink>
      </div>
    </nav>
  </header>
</template>
